import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { projects } from '../api/fee'
import { QueryState } from '../components/common'
import { ProjectCtx, useProject } from './projectContext'

/** Every legacy project page did `projectService.view(projectId)` on init
 * and gated its Edit/Add controls on `project.isAdmin` (admin spec, common
 * patterns). Done once here for the whole `/projects/:projectId/*` subtree. */
export function ProjectProvider({ children }: { children: ReactNode }) {
  const { projectId = '' } = useParams<{ projectId: string }>()
  const q = useApiQuery(() => projects.view(projectId), [projectId])
  return (
    <QueryState loading={q.loading && !q.data} error={q.error}>
      {q.data && (
        <ProjectCtx.Provider value={{ projectId, project: q.data, isAdmin: Boolean(q.data.isAdmin), refetch: q.refetch }}>
          {children}
        </ProjectCtx.Provider>
      )}
    </QueryState>
  )
}

/** Route guard for `/projects/:projectId/admin/*` and the admin-only
 * catalogue/settings pages. The legacy app had none (audit §10 D1: every
 * admin URL rendered for anyone, only the API calls failed). The backend
 * stays the real authorization boundary (ROLE_OWNER/ROLE_FULL); this only
 * stops a non-admin member from seeing a broken admin screen. */
export function RequireProjectAdmin({ children }: { children: ReactNode }) {
  const { isAdmin } = useProject()
  if (!isAdmin) {
    return (
      <p role="alert" className="portal-notice portal-notice--error">
        Admin access to this project is required for this page.
      </p>
    )
  }
  return <>{children}</>
}
